import { getConfig, klinikenApi } from "@/api";
import {
    Config,
    ConfigModuleForm,
    ConfigModuleName,
    ConfigSubModuleName,
    ConfigType,
} from "@/store/modules/types/configModuleTypes";

type Commit = (
    event: string,
    payload?: unknown,
    options?: {
        root?: boolean;
    }
) => void;

type State = {
    [ConfigModuleName.Core]?: Config;
    [ConfigModuleName.Operation]?: Config;
};

const getDefaultState: () => State = () => {
    return {};
};

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        /**
         * Hämta config baserat på module name (t.ex operation) och sub module name (optional) (t.ex
         * OPERATION_ANMALAN)
         */
        configByName:
            (state: State) =>
            ({ moduleName, subModuleName }: { moduleName: ConfigModuleName; subModuleName?: ConfigSubModuleName }) => {
                if (subModuleName) {
                    return state[moduleName]?.[subModuleName];
                }

                return state[moduleName];
            },

        /**
         * Returnerar om en specifik komponent ska renderas eller inte
         */
        shouldBeVisibleByName:
            (state: State) =>
            ({
                moduleName,
                subModuleName,
                name,
                type = ConfigType.Field,
                overrideValue,
            }: {
                name: string;
                moduleName: ConfigModuleName;
                subModuleName: ConfigSubModuleName;
                type?: ConfigType;
                overrideValue?: boolean;
            }) => {
                if (typeof overrideValue === "boolean") {
                    return overrideValue;
                }

                const shouldBeVisible = (state[moduleName]?.[subModuleName] as ConfigModuleForm)?.[type]?.[name]
                    ?.visible;

                if (typeof shouldBeVisible === "boolean") {
                    return shouldBeVisible;
                }

                // Default är att visa komponenten, om inte visible specifikt satts till false
                return true;
            },
        /**
         * Returnerar om ett specifikt fält ska vara required eller inte i ett formulär
         */
        shouldBeRequiredByName:
            (state: State) =>
            ({
                moduleName,
                subModuleName,
                name,
                defaultRequired = false,
            }: {
                name: string;
                moduleName: ConfigModuleName;
                subModuleName: ConfigSubModuleName;
                defaultRequired?: boolean;
            }) => {
                const shouldBeRequired = (state[moduleName]?.[subModuleName] as ConfigModuleForm)?.[ConfigType.Field]?.[
                    name
                ]?.required;

                if (typeof shouldBeRequired === "boolean") {
                    return shouldBeRequired;
                }

                // Sätter required till default false, om inte ett annat värde skickats in
                return defaultRequired;
            },
        /**
         * Hämtar index (nummer, 1 indexed) på en sektion, baserat på hämtade inställningar och vilka sektioner som är
         * synliga
         */
        visibleSectionIndex:
            (state: State) =>
            ({
                moduleName,
                subModuleName,
                name,
            }: {
                moduleName: ConfigModuleName;
                subModuleName: ConfigSubModuleName;
                name: string;
            }) => {
                const sections = (state[moduleName]?.[subModuleName] as ConfigModuleForm)?.[ConfigType.Section];

                if (!sections) {
                    return 1;
                }

                return (
                    Object.entries(sections)
                        .filter(([_, section]) => section.visible)
                        .findIndex(([key]) => key === name) + 1
                );
            },
        maxIdle:
            (state: State) =>
            ({ moduleName, subModuleName }: { moduleName: ConfigModuleName; subModuleName: ConfigSubModuleName }) => {
                const maxIdleMinutes = (state[moduleName]?.[subModuleName] as ConfigModuleForm)?.["maxIdleMinutes"];
                if (maxIdleMinutes) return maxIdleMinutes * 60 * 1000;
                return undefined;
            },
    },
    mutations: {
        reset: (state: State) => {
            Object.assign(state, getDefaultState());
        },
        addConfig: (
            state: State,
            {
                moduleName,
                config,
            }: {
                moduleName: ConfigModuleName;
                config: State[ConfigModuleName];
            }
        ) => {
            state[moduleName] = config;
        },
    },
    actions: {
        /**
         * Hämta inställningar baserat på module name (t.ex opertion) och stoppa in i store.
         * Använd getters för att hämta och lyssna på förändringar av configen
         */
        fetchInstallningar: async (
            {
                commit,
                state,
            }: {
                commit: Commit;
                state: State;
            },
            {
                moduleName,
            }: {
                moduleName: ConfigModuleName;
            }
        ) => {
            try {
                const apiUrl = `${moduleName}/installningar/`;

                const response = await klinikenApi.get(apiUrl, getConfig({}));

                commit("addConfig", {
                    moduleName,
                    config: response.data,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
